import { useCallback } from 'react'

import { Icon, MenuV2 } from '@circlefin/components'
import {
  BlockchainRecipientAddressOwner,
  BlockchainRecipientAddressesWithMetadataDocument,
  useSetBlockchainRecipientAddressWithMetadataMutation,
} from '@shared/graphql'
import { setBlockchainRecipientAddressWithMetadata as setBlockchainRecipientAddressWithMetadataUpdate } from '@shared/graphql.server/updates'
import useTranslation from 'next-translate/useTranslation'

import type { BlockchainRecipientAddressWithMetadata } from '@shared/graphql'

export interface AddressBookActionsMenuProps {
  /**
   * Blockchain recipient address with metadata.
   */
  recipientAddress: BlockchainRecipientAddressWithMetadata
}

export const AddressBookActionsMenu: React.FC<AddressBookActionsMenuProps> = ({
  recipientAddress,
}) => {
  const { t } = useTranslation('address-book/containers')

  const [setBlockchainRecipientAddressWithMetadata, { loading }] =
    useSetBlockchainRecipientAddressWithMetadataMutation({
      refetchQueries: [BlockchainRecipientAddressesWithMetadataDocument],
      update: setBlockchainRecipientAddressWithMetadataUpdate,
    })

  const toggleOwner = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      event.stopPropagation()

      const updatedOwner =
        recipientAddress.owner === BlockchainRecipientAddressOwner.customer
          ? BlockchainRecipientAddressOwner.non_customer_entity
          : BlockchainRecipientAddressOwner.customer

      void setBlockchainRecipientAddressWithMetadata({
        variables: {
          input: {
            id: recipientAddress.id,
            owner: updatedOwner,
          },
        },
      })
    },
    [
      recipientAddress.id,
      recipientAddress.owner,
      setBlockchainRecipientAddressWithMetadata,
    ],
  )

  if (loading) {
    return (
      <Icon className="animate-spin text-blue-300" name="CircleSpinnerSolid" />
    )
  }

  return (
    <MenuV2 className="mx-auto">
      <MenuV2.IconButton label={t`common:options`} variant="secondary" />
      <MenuV2.Options>
        <MenuV2.Item
          className="w-9"
          item={{
            label: t(`address-book-action-menu.${recipientAddress.owner}`),
            value: recipientAddress.owner,
          }}
          onClick={toggleOwner}
        />
      </MenuV2.Options>
    </MenuV2>
  )
}
